import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Thermometer,
  BarChart3,
  Settings,
  X,
  Droplets,
  Battery,
  Wifi,
  WifiOff,
  LogOut
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = "https://iudzzeqvihyxbzdvooeq.supabase.co";
const supabaseAnonKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml1ZHp6ZXF2aWh5eGJ6ZHZvb2VxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY5Nzc4MDcsImV4cCI6MjA3MjU1MzgwN30.KcSOr8Oa0fnSaeWWBNXZy0osMOdL2od_tJ3vOV_-c44";
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [devices, setDevices] = useState([]);
  const [expandedDeviceIds, setExpandedDeviceIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const fetchData = async () => {
    // Ne mets plus loading à true après premier chargement
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const fullName = user.user_metadata?.full_name || '';
      let prenom = '';
      let nom = '';
      if (fullName) {
        const parts = fullName.trim().split(' ');
        if (parts.length === 1) prenom = capitalize(parts[0]);
        else if (parts.length >= 2) {
          prenom = capitalize(parts[0]);
          nom = parts.slice(1).map(capitalize).join(' ');
        }
      }
      setProfile({ prenom, nom });

      const { data, error } = await supabase
        .from('devices')
        .select('*, sensors(*)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (data) setDevices(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      fetchData();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const toggleDevice = (deviceId) => {
    setExpandedDeviceIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deviceId)) newSet.delete(deviceId);
      else newSet.add(deviceId);
      return newSet;
    });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  const navigation = [
    { name: 'Tableau de bord', href: '/', icon: Home },
    { name: 'Analytiques', href: '/analytics', icon: BarChart3 },
    { name: 'Paramètres', href: '/settings', icon: Settings },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <Wifi className="h-4 w-4 text-success-600" />;
      case 'warning': return <Wifi className="h-4 w-4 text-warning-600" />;
      case 'offline': return <WifiOff className="h-4 w-4 text-danger-600" />;
      default: return <WifiOff className="h-4 w-4 text-slate-400" />;
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 glass-effect transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:inset-0
      `}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-xl">
                <Droplets className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gradient">JuniAtmo</h1>
                <div className="flex items-center space-x-2 mt-1">
                  {profile ? (
                    <span className="text-xs text-slate-700 font-semibold">
                      {profile.prenom} {profile.nom}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">Chargement...</span>
                  )}
                  <button
                    onClick={handleLogout}
                    title="Se déconnecter"
                    className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-indigo-200 to-primary-100 shadow hover:scale-110 transition-all border border-indigo-300 ml-1"
                    aria-label="Se déconnecter"
                  >
                    <LogOut className="w-4 h-4 text-indigo-600" />
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            <div className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`nav-link ${isActive ? 'active' : ''}`}
                    onClick={onClose}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </div>

            {/* Devices */}
            <div className="pt-6">
              <h3 className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                Appareils connectés
              </h3>
              {loading && devices.length === 0 ? (
                <p className="px-4 text-xs text-slate-400">Chargement des appareils...</p>
              ) : devices.length === 0 ? (
                <p className="px-4 text-xs text-slate-400">Aucun appareil trouvé.</p>
              ) : (
                devices.map((device) => (
                  <div
                    key={device.id}
                    className="px-4 py-2 border border-white/10 rounded-lg mb-2"
                  >
                    <button
                      className="flex items-center justify-between w-full text-sm font-semibold text-indigo-700"
                      onClick={() => toggleDevice(device.id)}
                      aria-expanded={expandedDeviceIds.has(device.id)}
                      aria-controls={`device-sensors-${device.id}`}
                      type="button"
                    >
                      <span className="truncate">
                        {device.name} - {device.location}
                      </span>
                      <span className="ml-2 text-xs text-slate-500 cursor-pointer select-none">
                        {expandedDeviceIds.has(device.id) ? '▼' : '▶'}
                      </span>
                    </button>

                    {expandedDeviceIds.has(device.id) && (
                      <div
                        id={`device-sensors-${device.id}`}
                        className="mt-2 pl-4 space-y-1 max-h-48 overflow-auto"
                      >
                        {device.sensors && device.sensors.length > 0 ? (
                          device.sensors.map((sensor) => (
                            <Link
                              key={sensor.id}
                              to={`/sensor/${sensor.id}`}
                              className="flex items-center justify-between text-xs px-2 py-1 rounded hover:bg-indigo-100 transition-colors"
                              onClick={onClose}
                            >
                              <span>{sensor.name}</span>
                              {getStatusIcon(sensor.status)}
                            </Link>
                          ))
                        ) : (
                          <p className="px-2 py-1 text-xs italic text-slate-400">
                            Aucun capteur.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-white/10">
            <div className="bg-gradient-to-r from-primary-50 to-indigo-50 rounded-xl p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-primary-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Système actif</p>
                  <p className="text-xs text-slate-500">
                    {devices.reduce((sum, d) => sum + (d.status === 'active' ? 1 : 0), 0)}/{devices.length} appareils
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Sidebar;
